import React from "react";
import {
  Box,
  useTheme,
  Switch,
  Slider,
  Card,
  CardContent,
  Avatar,
  Collapse,
} from "@mui/material";
import { Search as SearchIcon } from "@mui/icons-material";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import AppButton from "../../../../../../views/components/common/buttons/AppButton";
import AppDropDown from "../../../../../../views/components/common/filter/AppDropDown";
import FormTextField from "../../../../../../views/components/common/form/FormTextField";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import AddProductsModal from "./AddProductsModal";
import { observer } from "mobx-react-lite";
import { useStores } from "../../../../use-stores";
// import { useNotification } from "../../../../utils/NotificationsContext";
import { getPackDisplayImage } from "../../../../../../utils/packImageUtils";
import { EmployeeRoles } from "../../../../../../models/Employees/EmployeeRoles";

interface ProductSettingsProps {
  onSettingChange: (key: string, value: any) => void;
  onSaveChanges: () => void;
  onSearchTermChange: (term: string) => void;
  onCategoryChange: (category: string) => void;
  onProductIdChange: (id: number | null) => void;
  onBulkUpdateToggle: (enabled: boolean) => void;
  onMarkupPercentageChange: (percentage: number) => void;
  onDiscountUpdateToggle: (enabled: boolean) => void;
  onDiscountPercentageChange: (percentage: number) => void;
  onModalOpenChange: (open: boolean) => void;
  onPriceChange: (productId: number, price: number | null) => void;
  onBulkPricingApply: () => void;
  onBulkDiscountApply: () => void;
  onResetPricing: () => void;
  onAddProducts: () => void;
  productSaveNotice?: string;
  onDownloadProductsPacksCSV: () => void;
  onUploadProductsPacksCSV: (file: File) => void;
}

const ProductSettings: React.FC<ProductSettingsProps> = ({
  onSettingChange,
  onSaveChanges,
  onSearchTermChange,
  onCategoryChange,
  onProductIdChange,
  onBulkUpdateToggle,
  onMarkupPercentageChange,
  onDiscountUpdateToggle,
  onDiscountPercentageChange,
  onModalOpenChange,
  onPriceChange,
  onBulkPricingApply,
  onBulkDiscountApply,
  onResetPricing,
  onAddProducts,
  productSaveNotice = "",
  onDownloadProductsPacksCSV,
  onUploadProductsPacksCSV,
}) => {
  const theme = useTheme();
  const { swagStoreStore, userStore } = useStores();
  // const { showError } = useNotification();
  // const fileInputRef = useRef<HTMLInputElement>(null);

  const {
    productSearchTerm,
    selectedCategory,
    selectedProductId,
    modalOpen,
    baseProductsToDisplay,
    settings,
    categories,
  } = swagStoreStore;

  // Build category list with "All" as first option
  const categoryList = [
    { id: "All", value: "All" },
    ...categories.map((cat) => ({
      id: cat.category_name,
      value: cat.category_name,
    })),
  ];

  // Handle file selection for upload
  // const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
  //   const file = event.target.files?.[0];
  //   if (!file) return;

  //   if (file.type !== "text/csv" && !file.name.endsWith(".csv")) {
  //     showError("Error", "Please upload a CSV file");
  //     return;
  //   }

  //   onUploadProductsPacksCSV(file);
  //   // Reset file input
  //   if (fileInputRef.current) {
  //     fileInputRef.current.value = "";
  //   }
  // };

  const filteredProducts =
    baseProductsToDisplay?.filter((item) => {
      const matchesSearch =
        !productSearchTerm ||
        (item.label &&
          item.label.toLowerCase().includes(productSearchTerm.toLowerCase()));

      const matchesCategory =
        selectedCategory === "All" || item.category_name === selectedCategory;

      return matchesSearch && matchesCategory;
    }) ?? [];

  return (
    <>
      <Box>
        <Box
          sx={{
            boxShadow: `${theme.palette.customColors.shadowVeryLight} 0px 6px 24px 0px, ${theme.palette.customColors.shadowLightBorder} 0px 0px 0px 1px`,
            p: 2.2,
            borderRadius: 2,
          }}
        >
          {/* Products Section */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1.5,
            }}
          >
            <CustomTypography
              variant="heading7"
              fontWeight={700}
              style={{ marginBottom: 0 }}
            >
              Products ({filteredProducts.length})
            </CustomTypography>
            {userStore.hasPermission(
              EmployeeRoles.ADD_EDIT_PRODUCTS_IN_SWAGSTORE,
            ) && (
              <AppButton
                label="+ Add more"
                onClick={() => onModalOpenChange(true)}
                variant="grayscale"
                customStyles={{
                  textTransform: "none",
                  borderColor: theme.palette.customColors?.white,
                  color: theme.palette.customColors?.blackText,
                  fontSize: "0.875rem",
                  padding: "6px 16px",
                  backgroundColor: theme.palette.customColors?.white,
                  "&:hover": {
                    borderColor: theme.palette.customColors?.white,
                    backgroundColor: theme.palette.customColors?.white,
                    color: theme.palette.customColors?.blackBorder,
                  },
                }}
              />
            )}
          </Box>

          {/* Search and Filter */}
          <Box
            sx={{
              display: "flex",
              gap: 2,
            }}
          >
            <Box sx={{ flex: 1 }}>
              <FormTextField
                placeholder="Search here..."
                value={productSearchTerm}
                onChange={(e, value) => onSearchTermChange(value || "")}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    "& fieldset": {
                      borderColor: theme.palette.customColors.greyBorder,
                    },
                  },
                  marginBottom: "0px",
                }}
                startAdornment={
                  <SearchIcon sx={{ color: theme.palette.grey[500] }} />
                }
              />
            </Box>

            <Box sx={{ minWidth: "86px" }}>
              <AppDropDown
                title=""
                isMultiSelect={false}
                value={selectedCategory}
                list={categoryList}
                onSingleSelectionChange={(id) => onCategoryChange(id)}
                fontSize="0.875rem"
                borderColor="#E0E0E0"
                borderRadius="8px"
                height="35.6px"
              />
            </Box>
          </Box>

          {/* Product Cards */}
          <Box sx={{ mt: 3, mb: 3 }}>
            {filteredProducts.map((item) => {
              // Get price from local state if user has edited it, otherwise use the item's price
              const editedPrice =
                settings.productMapping.productPrices[item.id];
              const currentPrice =
                editedPrice !== undefined
                  ? editedPrice
                  : (item.pack_price ?? 0);
              const basePriceFloor =
                item.pack_cost_price != null &&
                !Number.isNaN(Number(item.pack_cost_price))
                  ? Number(item.pack_cost_price)
                  : undefined;
              const isSelected = selectedProductId === item.id;

              return (
                <Card
                  key={item.id}
                  sx={{
                    mb: 2,
                    borderRadius: "12px",
                    border: isSelected
                      ? `2px solid ${
                          theme.palette.customColors?.purple || "#8B5CF6"
                        }`
                      : `1px solid ${theme.palette.customColors.lightGray}`,
                    boxShadow: `0 2px 8px ${theme.palette.customColors.shadowMedium}`,
                    cursor: "pointer",
                    "&:hover": {
                      borderColor: isSelected
                        ? theme.palette.customColors?.purple
                        : theme.palette.divider,
                    },
                    backgroundColor: theme.palette.customColors.cardBackground,
                  }}
                  onClick={() => onProductIdChange(item.id)}
                >
                  <CardContent
                    sx={{
                      p: 2,
                      "&.MuiCardContent-root:last-child": {
                        paddingBottom: "16px !important",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        gap: 2,
                        alignItems: "flex-start",
                      }}
                    >
                      {/* Product Image */}
                      <Avatar
                        src={getPackDisplayImage(item)}
                        alt={item.label}
                        variant="rounded"
                        sx={{
                          width: 50,
                          height: 50,
                          borderRadius: "8px",
                          backgroundColor:
                            theme.palette.customColors.greyBackground,
                          p: 1,
                        }}
                      />

                      {/* Product Details */}
                      <Box sx={{ flex: 1 }}>
                        <CustomTypography
                          variant="body3"
                          fontWeight={400}
                          style={{
                            marginBottom: "2px",
                            fontSize: "0.75rem",
                            lineHeight: "1.25",
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            wordBreak: "break-word",
                            whiteSpace: "normal",
                            maxHeight: "2.5em",
                            minHeight: "2.5em",
                          }}
                        >
                          {item.label}
                        </CustomTypography>
                        {/* <CustomTypography
                          variant="body3"
                          style={{
                            color: theme.palette.grey[600],
                            marginBottom: "6px",
                          }}
                        > */}
                        {/* Cost Price: ₹{item.pack_price?.toFixed(2) ?? 0} */}
                        {/* • Margin: {0}% */}
                        {/* </CustomTypography> */}

                        {/* Selling Price Input */}
                        <Box sx={{ mb: 1, marginBottom: "0px !important" }}>
                          <Box>
                            <CustomTypography
                              variant="body3"
                              fontWeight={400}
                              style={{
                                color: theme.palette.grey[800],
                              }}
                            >
                              Available qty: {item.available_quantity}
                            </CustomTypography>
                          </Box>
                          <Box>
                            <CustomTypography
                              variant="body3"
                              fontWeight={400}
                              style={{
                                color: theme.palette.grey[800],
                                marginTop: "2px",
                              }}
                            >
                              Base Price: {item.pack_cost_price}
                            </CustomTypography>
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              mb: isSelected ? 0.5 : 0,
                            }}
                          >
                            <CustomTypography
                              variant="body3"
                              fontWeight={400}
                              style={{
                                color: theme.palette.grey[800],
                              }}
                            >
                              Selling Price
                            </CustomTypography>

                            <FormTextField
                              type="number"
                              value={currentPrice ?? ""}
                              onChange={(id, value) => {
                                if (
                                  !userStore.hasPermission(
                                    EmployeeRoles.ADD_EDIT_PRODUCTS_IN_SWAGSTORE,
                                  )
                                )
                                  return;
                                if (
                                  value === "" ||
                                  value === undefined ||
                                  value === null
                                ) {
                                  onPriceChange(item.id, null);
                                } else {
                                  const price = Number(value);
                                  onPriceChange(
                                    item.id,
                                    isNaN(price) ? null : price,
                                  );
                                }
                              }}
                              onBlur={(e) => {
                                if (
                                  !userStore.hasPermission(
                                    EmployeeRoles.ADD_EDIT_PRODUCTS_IN_SWAGSTORE,
                                  ) ||
                                  basePriceFloor === undefined
                                ) {
                                  return;
                                }
                                const raw = e.target.value;
                                if (raw === "") return;
                                const price = Number(raw);
                                if (isNaN(price) || price >= basePriceFloor)
                                  return;
                                onPriceChange(item.id, basePriceFloor);
                              }}
                              disabled={
                                !userStore.hasPermission(
                                  EmployeeRoles.ADD_EDIT_PRODUCTS_IN_SWAGSTORE,
                                )
                              }
                              sx={{
                                backgroundColor:
                                  theme.palette.customColors.lightGreyColor,
                                marginBottom: "0px",
                                width: "81px",
                                height: "20px",
                                borderRadius: "4px",
                                "& .MuiInputBase-input": {
                                  paddingTop: "1px ",
                                  marginLeft: "-6px",
                                },
                                "& .MuiInputBase-root": {
                                  borderBottom: "none",
                                  "&::before": {
                                    borderBottom: "none",
                                  },
                                  "&::after": {
                                    borderBottom: "none",
                                  },
                                  "&:hover:not(.Mui-disabled):before": {
                                    borderBottom: "none",
                                  },
                                },
                              }}
                              variant="standard"
                              borderColor={theme.palette.customColors.white}
                              startAdornment={
                                <CurrencyRupeeIcon
                                  sx={{
                                    color: theme.palette.grey[500],
                                    height: "13px",
                                    width: "13px",
                                    marginLeft: "12px",
                                    marginTop: "-4px",
                                  }}
                                />
                              }
                            ></FormTextField>
                          </Box>

                          {/* Update Button - Only for selected product */}
                          {isSelected &&
                            userStore.hasPermission(
                              EmployeeRoles.ADD_EDIT_PRODUCTS_IN_SWAGSTORE,
                            ) && (
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "flex-end",
                                  p: 0.8,
                                  pb: 0,
                                }}
                              >
                                <AppButton
                                  label="Update"
                                  onClick={() => {
                                    const priceForSave =
                                      basePriceFloor !== undefined &&
                                      currentPrice != null
                                        ? Math.max(
                                            currentPrice,
                                            basePriceFloor,
                                          )
                                        : currentPrice;
                                    onSettingChange?.("updateProduct", {
                                      id: item.id,
                                      price: priceForSave,
                                    });
                                  }}
                                  variant="primaryBlock"
                                  customStyles={{
                                    backgroundColor:
                                      theme.palette.customColors?.purple,
                                    "&:hover": {
                                      backgroundColor:
                                        theme.palette.customColors?.purple,
                                    },
                                    borderRadius: "8px",
                                    minHeight: "30px",
                                    maxHeight: "30px",
                                    width: "81px",
                                    minWidth: "81px",
                                  }}
                                />
                              </Box>
                            )}
                        </Box>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        </Box>

        {/* Bulk Price Updates Section */}
        {userStore.hasPermission(
          EmployeeRoles.ADD_EDIT_PRODUCTS_IN_SWAGSTORE,
        ) && (
          <Box
            sx={{
              mt: 3,
              mb: 10,
              boxShadow: `${theme.palette.customColors.shadowVeryLight} 0px 6px 24px 0px, ${theme.palette.customColors.shadowLightBorder} 0px 0px 0px 1px`,
              p: 2.2,
              borderRadius: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 0,
              }}
            >
              <CustomTypography
                variant="body1"
                fontWeight={700}
                style={{ marginBottom: 0 }}
              >
                Bulk Price Updates
              </CustomTypography>
              <Switch
                checked={settings.productMapping.bulkUpdateEnabled}
                onClick={() =>
                  onBulkUpdateToggle(!settings.productMapping.bulkUpdateEnabled)
                }
                sx={{
                  "& .MuiSwitch-switchBase": {
                    "&.Mui-checked": {
                      color: theme.palette.customColors?.purple || "#9333EA",
                      "& + .MuiSwitch-track": {
                        backgroundColor:
                          theme.palette.customColors?.purple || "#9333EA",
                      },
                    },
                  },
                  "& .MuiSwitch-track": {
                    backgroundColor: theme.palette.customColors.switchTrackGray,
                    opacity: 1,
                  },
                }}
              />
            </Box>

            <CustomTypography
              variant="body2"
              style={{
                color: theme.palette.grey[600],
                marginBottom: "14px",
                marginTop: "-4px",
              }}
            >
              Apply markup to selected products
            </CustomTypography>

            <Collapse in={settings.productMapping.bulkUpdateEnabled}>
              <Box>
                <CustomTypography
                  variant="body2"
                  fontWeight={400}
                  style={{ marginBottom: "10px" }}
                >
                  Markup Percentage: {settings.productMapping.markupPercentage}%
                </CustomTypography>

                <Box sx={{ mb: 2 }}>
                  <Slider
                    value={settings.productMapping.markupPercentage}
                    onChange={(_, newValue) =>
                      onMarkupPercentageChange(newValue as number)
                    }
                    min={0}
                    max={200}
                    step={1}
                    disabled={false}
                    sx={{
                      color: theme.palette.customColors?.purple || "#8B5CF6",
                      "& .MuiSlider-thumb": {
                        backgroundColor:
                          theme.palette.customColors?.purple || "#8B5CF6",
                        "&:hover": {
                          boxShadow: `0 0 0 8px rgba(${
                            theme.palette.customColors?.purple
                              ? "139, 92, 246"
                              : "139, 92, 246"
                          }, 0.16)`,
                        },
                      },
                      "& .MuiSlider-track": {
                        backgroundColor:
                          theme.palette.customColors?.purple || "#8B5CF6",
                      },
                      "& .MuiSlider-rail": {
                        backgroundColor: theme.palette.customColors.lightGray,
                      },
                    }}
                  />
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mt: 0.5,
                    }}
                  >
                    <CustomTypography
                      variant="body2"
                      style={{
                        color: theme.palette.grey[600],
                        fontSize: "0.75rem",
                      }}
                    >
                      0%
                    </CustomTypography>
                    <CustomTypography
                      variant="body2"
                      style={{
                        color: theme.palette.grey[600],
                        fontSize: "0.75rem",
                      }}
                    >
                      100%
                    </CustomTypography>
                    <CustomTypography
                      variant="body2"
                      style={{
                        color: theme.palette.grey[600],
                        fontSize: "0.75rem",
                      }}
                    >
                      200%
                    </CustomTypography>
                  </Box>
                </Box>

                <AppButton
                  label="Apply Bulk Pricing"
                  onClick={onBulkPricingApply}
                  variant="grayscale"
                  disabled={false}
                  customStyles={{
                    width: "100%",
                    textTransform: "none",
                    borderColor: theme.palette.divider,
                    color: theme.palette.text.primary,
                    fontSize: "0.875rem",
                    padding: "8px 16px",
                    backgroundColor: theme.palette.customColors.white,
                    "&:hover": {
                      borderColor: theme.palette.divider,
                      backgroundColor:
                        theme.palette.customColors.blackBodyBackgorund,
                      color: theme.palette.customColors.white,
                    },
                    "&:disabled": {
                      borderColor: theme.palette.divider,
                      color: theme.palette.action?.disabled,
                    },
                  }}
                />
              </Box>
            </Collapse>

            <Box sx={{ mt: 2.5 }}>
              <CustomTypography
                variant="body2"
                style={{
                  color: theme.palette.grey[600],
                  marginBottom: "10px",
                }}
              >
                Revert all selling prices for this store to their default values
                (server reset).
              </CustomTypography>
              <AppButton
                label="Reset all prices"
                onClick={onResetPricing}
                variant="grayscale"
                customStyles={{
                  width: "100%",
                  textTransform: "none",
                  borderColor: theme.palette.divider,
                  color: theme.palette.text.primary,
                  fontSize: "0.875rem",
                  padding: "8px 16px",
                  backgroundColor: theme.palette.customColors.white,
                  "&:hover": {
                    borderColor: theme.palette.divider,
                    backgroundColor:
                      theme.palette.customColors.blackBodyBackgorund,
                    color: theme.palette.customColors.white,
                  },
                }}
              />
            </Box>
          </Box>
        )}

        {/* Bulk discount UI: disabled via `false &&` — delete `false &&` to re-enable */}
        {false &&
          userStore.hasPermission(
            EmployeeRoles.ADD_EDIT_PRODUCTS_IN_SWAGSTORE,
          ) && (
            <Box
              sx={{
                mt: 3,
                boxShadow: `${theme.palette.customColors.shadowVeryLight} 0px 6px 24px 0px, ${theme.palette.customColors.shadowLightBorder} 0px 0px 0px 1px`,
                p: 2.2,
                borderRadius: 2,
                mb: 10,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 0,
                }}
              >
                <CustomTypography
                  variant="body1"
                  fontWeight={700}
                  style={{ marginBottom: 0 }}
                >
                  Bulk Discount
                </CustomTypography>
                <Switch
                  checked={settings.productMapping.discountUpdateEnabled}
                  onClick={() =>
                    onDiscountUpdateToggle(
                      !settings.productMapping.discountUpdateEnabled,
                    )
                  }
                  sx={{
                    "& .MuiSwitch-switchBase": {
                      "&.Mui-checked": {
                        color: theme.palette.customColors?.purple || "#9333EA",
                        "& + .MuiSwitch-track": {
                          backgroundColor:
                            theme.palette.customColors?.purple || "#9333EA",
                        },
                      },
                    },
                    "& .MuiSwitch-track": {
                      backgroundColor:
                        theme.palette.customColors.switchTrackGray,
                      opacity: 1,
                    },
                  }}
                />
              </Box>

              <CustomTypography
                variant="body2"
                style={{
                  color: theme.palette.grey[600],
                  marginBottom: "14px",
                  marginTop: "-4px",
                }}
              >
                Apply discount to all products
              </CustomTypography>

              <Collapse in={settings.productMapping.discountUpdateEnabled}>
                <Box>
                  <CustomTypography
                    variant="body2"
                    fontWeight={400}
                    style={{ marginBottom: "10px" }}
                  >
                    Discount Percentage:{" "}
                    {settings.productMapping.discountPercentage}%
                  </CustomTypography>

                  <Box sx={{ mb: 2 }}>
                    <Slider
                      value={settings.productMapping.discountPercentage}
                      onChange={(_, newValue) =>
                        onDiscountPercentageChange(newValue as number)
                      }
                      min={0}
                      max={100}
                      step={1}
                      disabled={false}
                      sx={{
                        color:
                          theme.palette.customColors?.purple || "#8B5CF6",
                        "& .MuiSlider-thumb": {
                          backgroundColor:
                            theme.palette.customColors?.purple || "#8B5CF6",
                          "&:hover": {
                            boxShadow: `0 0 0 8px rgba(${
                              theme.palette.customColors?.purple
                                ? "139, 92, 246"
                                : "139, 92, 246"
                            }, 0.16)`,
                          },
                        },
                        "& .MuiSlider-track": {
                          backgroundColor:
                            theme.palette.customColors?.purple || "#8B5CF6",
                        },
                        "& .MuiSlider-rail": {
                          backgroundColor:
                            theme.palette.customColors.lightGray,
                        },
                      }}
                    />
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mt: 0.5,
                      }}
                    >
                      <CustomTypography
                        variant="body2"
                        style={{
                          color: theme.palette.grey[600],
                          fontSize: "0.75rem",
                        }}
                      >
                        0%
                      </CustomTypography>
                      <CustomTypography
                        variant="body2"
                        style={{
                          color: theme.palette.grey[600],
                          fontSize: "0.75rem",
                        }}
                      >
                        50%
                      </CustomTypography>
                      <CustomTypography
                        variant="body2"
                        style={{
                          color: theme.palette.grey[600],
                          fontSize: "0.75rem",
                        }}
                      >
                        100%
                      </CustomTypography>
                    </Box>
                  </Box>

                  <AppButton
                    label="Apply Bulk Discount"
                    onClick={onBulkDiscountApply}
                    variant="grayscale"
                    disabled={false}
                    customStyles={{
                      width: "100%",
                      textTransform: "none",
                      borderColor: theme.palette.divider,
                      color: theme.palette.text.primary,
                      fontSize: "0.875rem",
                      padding: "8px 16px",
                      backgroundColor: theme.palette.customColors.white,
                      "&:hover": {
                        borderColor: theme.palette.divider,
                        backgroundColor:
                          theme.palette.customColors.blackBodyBackgorund,
                        color: theme.palette.customColors.white,
                      },
                      "&:disabled": {
                        borderColor: theme.palette.divider,
                        color: theme.palette.action?.disabled,
                      },
                    }}
                  />
                </Box>
              </Collapse>
            </Box>
          )}
      </Box>

      {/* Add Products Modal */}
      <AddProductsModal
        open={modalOpen}
        onClose={() => onModalOpenChange(false)}
        onAddProducts={onAddProducts}
        saveNotice={productSaveNotice}
      />
    </>
  );
};

export default observer(ProductSettings);
